import {
  IChartProps,
  SystemPerformanceResponseModel,
} from "../../system-performance/models/chart";
import { useEffect, useMemo, useState } from "react";

import ApexOptions from "../../../../../common/components/apexchart/ApexChartModels";
import { DEFAULT_STATISTICS_DATE_FORMAT } from "../common/values";
import { createChartTooltip } from "../common/chartTooltip";
import dayjs from "dayjs";
import defaultApexOptions from "../common/defaultApexOptions";
import { eventType } from "../../../../../API";
import { nameParser } from "../../../../../common/utils/nameParser";
import uniq from "lodash/uniq";
import { useGetStatisticsData } from "./useGetStatisticsData";
import { useTheme } from "@mui/material";

export const useCreateSystemHealthChart = <
  T extends SystemPerformanceResponseModel
>(
  timeframeDays: number,
  locations: string[] | null,
  eventTypeActual: eventType,
  jobId?: string
): IChartProps<T> => {
  const { loading, data, refetching, refetchChartData } = useGetStatisticsData<
    T[]
  >({
    eventType: eventTypeActual,
    models: [],
    locations,
    timeframeDays,
    jobId,
  });

  const theme = useTheme();

  const options = defaultApexOptions(theme);

  const [chartSeries, setChartProps] = useState<
    ApexAxisChartSeries | ApexNonAxisChartSeries | undefined
  >([]);
  const [chartOptions, setChartOptions] = useState<ApexOptions>(options);

  useEffect(() => {
    const series = generateChartSeries(data);
    const extendedOptions = generateChartCategories(data);

    const fixedSeries = Array.isArray(series)
      ? series.map(s => ({
          ...s,
          data: Array.isArray(s.data)
            ? s.data.map(d => (d === undefined ? null : d))
            : s.data,
        }))
      : series;

    setChartProps(fixedSeries);

    setChartOptions(extendedOptions);
  }, [data, theme.palette.mode]);

  const tooltip = useMemo(() => createChartTooltip({ percent: true }), []);

  const generateChartSeries = (response: T[]) => {
    if (!Array.isArray(response) || response.length === 0) {
      return [];
    }

    const labels = uniq(
      response.map(el => {
        if (el.event_type === eventType.bandwidthFailures) {
          return new Date(el.day_start).getTime();
        } else {
          return new Date(el.week_start).getTime();
        }
      })
    ).sort((a, b) => a - b);

    const uniqueItemsIds = uniq(response.map(el => el.id));

    const series = uniqueItemsIds.map(itemId => {
      const item = response.find(el => el.id === itemId);

      const seriesData = labels.map(date => {
        const entry = response.find(el => {
          const dateField =
            el.event_type === eventType.bandwidthFailures
              ? el.day_start
              : el.week_start;

          return el.id === itemId && new Date(dateField).getTime() === date;
        });

        if (entry && "uptime_pct" in entry) {
          return entry ? Math.round(parseFloat(entry.uptime_pct ?? 0)) : 0;
        }

        if (entry && "intervals_with_failures_pct" in entry) {
          return entry
            ? Math.round(parseFloat(entry.intervals_with_failures_pct ?? 0))
            : 0;
        }
      });

      let name = "";

      const itemEventType = item?.event_type;

      if (
        itemEventType === eventType.nodeUptime ||
        itemEventType === eventType.bandwidthFailures
      ) {
        name = nameParser.node(itemId);
      }

      if (itemEventType === eventType.deviceUptime) {
        name = nameParser.device(itemId);
      }

      if (itemEventType === eventType.serviceUptime) {
        name = nameParser.serviceWithAbbreviation(itemId);
      }

      return {
        name,
        data: seriesData,
        type:
          seriesData.filter(val => val !== null).length === 1
            ? "scatter"
            : "line",
      };
    });

    return series;
  };

  const generateChartCategories = (response: T[]): ApexOptions => {
    if (!Array.isArray(response) || response.length === 0) {
      return options;
    }

    const labels = uniq(
      response
        .sort((a, b) => {
          const dateFieldA =
            a.event_type === eventType.bandwidthFailures
              ? a.day_start
              : a.week_start;

          const dateFieldB =
            b.event_type === eventType.bandwidthFailures
              ? b.day_start
              : b.week_start;

          return (
            new Date(dateFieldA).getTime() - new Date(dateFieldB).getTime()
          );
        })
        .map(s => {
          const dateField =
            s.event_type === eventType.bandwidthFailures
              ? s.day_start
              : s.week_start;

          return dayjs(dateField).format(DEFAULT_STATISTICS_DATE_FORMAT);
        })
    );

    const extendedOptions: ApexOptions = {
      ...options,
      tooltip,
      stroke: {
        ...options.stroke,
        width: chartSeries?.length === 1 ? 4 : 2,
      },
      legend: {
        showForZeroSeries: true,
        showForNullSeries: true,
      },
      yaxis: {
        ...options.yaxis,
        max: 100,
        labels: {
          formatter: val => `${val}%`,
        },
      },
      xaxis: {
        type: "datetime",
        categories: labels,
        tickAmount: 14,
        tooltip: {
          enabled: false,
        },
        labels: {
          rotate: -70,
          showDuplicates: false,
          formatter: function (value: string) {
            return dayjs(value).format(DEFAULT_STATISTICS_DATE_FORMAT);
          },
        },
      },
    };

    return extendedOptions;
  };

  return {
    series: chartSeries,
    options: chartOptions,
    loading,
    originalData: data,
    refetching,
    showNoData: !loading && !refetching && data.length === 0,
    refetchChartData,
  };
};
